import { Location, CommonModule } from "@angular/common";
import { DOCUMENT } from "@angular/common";
import {
  Component,
  HostListener,
  computed,
  inject,
  signal,
  DestroyRef,
  Renderer2,
  effect,
} from "@angular/core";
import { afterNextRender, Injector } from "@angular/core";
import { toSignal, takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  Router,
  NavigationEnd,
  NavigationStart,
  NavigationCancel,
  NavigationError,
  RouterModule,
} from "@angular/router";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import PerfectScrollbar from "perfect-scrollbar";
import { asyncScheduler } from "rxjs";
import { filter, map, observeOn } from "rxjs/operators";

import { LayoutService } from "@services/layout.service";


import { FooterComponent } from "../../components/footer/footer.component";
import { NavbarComponent } from "../../components/navbar/navbar.component";
import { SidebarComponent } from "../../components/sidebar/sidebar.component";
import { LoadingService } from "./../../services/loading.service";

@Component({
  selector: "app-admin-layout",
  templateUrl: "./admin-layout.component.html",
  styleUrls: ["./admin-layout.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    SidebarComponent,
    NavbarComponent,
    FooterComponent,
    NgbModule,
  ],
})
export class AdminLayoutComponent {
  public location = inject(Location);
  public router = inject(Router);
  public loader = inject(LoadingService);
  public layoutService = inject(LayoutService);
  private destroyRef = inject(DestroyRef);
  private renderer = inject(Renderer2);
  private document = inject(DOCUMENT);
  private injector = inject(Injector);

  public lastPoppedUrl = signal<string | undefined>(undefined);
  public yScrollStack = signal<number[]>([]);

  // Signal che reagisce agli eventi del router
  private routerLoading = toSignal(
    this.router.events.pipe(
      filter(
        (event) =>
          event instanceof NavigationStart ||
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError,
      ),
      map((event) => event instanceof NavigationStart),
      observeOn(asyncScheduler),
    ),
    { initialValue: false },
  );

  // Segnale combinato per il caricamento
  isLoading = computed(() => this.routerLoading() || this.loader.isLoading());

  @HostListener("click", ["$event"])
  onGlobalClick(event: Event) {
    const target = event.target as HTMLElement;
    const isPaginationClick =
      target.closest(".mat-mdc-paginator-navigation-next") ||
      target.closest(".mat-mdc-paginator-navigation-previous") ||
      target.closest(".mat-mdc-paginator-navigation-first") ||
      target.closest(".mat-mdc-paginator-navigation-last") ||
      target.closest(".pagination") ||
      target.closest(".page-link") ||
      target.getAttribute("aria-label")?.includes("Page") ||
      target.innerText?.match(/^[0-9]+$/);

    if (isPaginationClick) {
      this.scrollToTop();
    }
  }

  constructor() {
    this.setupGlobalClasses();
    this.setupScrollRestoration();
    this.setupPerfectScrollbar();

    // Synchronize sidebar-mini state with body class
    effect(() => {
      const body = this.document.body;
      if (this.layoutService.isSidebarMini()) {
        this.renderer.addClass(body, "sidebar-mini");
      } else {
        this.renderer.removeClass(body, "sidebar-mini");
      }
    });
  }

  private scrollToTop() {
    afterNextRender(
      () => {
        const mainPanel = this.document.querySelector(
          ".main-panel",
        ) as HTMLElement;
        const activeTable = this.document.querySelector(
          "table[mat-table], mat-table, mat-paginator, ngb-pagination, table.table",
        );

        if (mainPanel && activeTable) {
          const card = activeTable.closest(".card") || activeTable;
          const headerOffset = 120;

          let actualTop = 0;
          let curr = card as HTMLElement;
          while (curr && curr !== mainPanel && mainPanel.contains(curr)) {
            actualTop += curr.offsetTop;
            curr = curr.offsetParent as HTMLElement;
          }

          const finalScrollPos = actualTop - headerOffset;
          mainPanel.scrollTo({ top: finalScrollPos, behavior: "smooth" });
        }
      },
      { injector: this.injector },
    );
  }

  private setupGlobalClasses() {
    const isWindows = navigator.platform.indexOf("Win") > -1;
    const html = this.document.documentElement;

    if (isWindows) {
      this.renderer.addClass(html, "perfect-scrollbar-on");
    } else {
      this.renderer.removeClass(html, "perfect-scrollbar-off");
    }
  }

  private setupScrollRestoration() {
    this.location.subscribe((ev) => {
      this.lastPoppedUrl.set(ev.url ?? "");
    });

    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          if (event.url !== this.lastPoppedUrl()) {
            this.yScrollStack.update((stack) => [...stack, window.scrollY]);
          }
        } else if (
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError
        ) {
          if (event instanceof NavigationEnd) {
            const elemMainPanel = this.document.querySelector(
              ".main-panel",
            ) as HTMLElement;
            const elemSidebar = this.document.querySelector(
              ".sidebar .sidebar-wrapper",
            ) as HTMLElement;

            if (event.url === this.lastPoppedUrl()) {
              this.lastPoppedUrl.set(undefined);
              const lastScroll = this.yScrollStack().pop();
              window.scrollTo(0, lastScroll || 0);
            } else {
              window.scrollTo(0, 0);
            }
            if (elemMainPanel) elemMainPanel.scrollTop = 0;
            if (elemSidebar) elemSidebar.scrollTop = 0;
          }
        }
      });
  }

  private setupPerfectScrollbar() {
    afterNextRender(
      () => {
        const elemMainPanel = this.document.querySelector(
          ".main-panel",
        ) as HTMLElement;
        const elemSidebar = this.document.querySelector(
          ".sidebar .sidebar-wrapper",
        ) as HTMLElement;

        if (window.matchMedia(`(min-width: 960px)`).matches && !this.isMac()) {
          if (elemMainPanel) new PerfectScrollbar(elemMainPanel);
          if (elemSidebar) new PerfectScrollbar(elemSidebar);
        }
      },
      { injector: this.injector },
    );
  }

  private isMac(): boolean {
    const platform = navigator.platform.toUpperCase();
    return platform.indexOf("MAC") >= 0 || platform.indexOf("IPAD") >= 0;
  }
}
